import React, { useState, useEffect } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { loan360Path } from '../utils/loanNavigation';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Landmark,
  Layers,
  RefreshCw,
  FileWarning,
  AlertTriangle,
  Users,
  Target,
  BarChart3,
  Radar,
  Building2,
  TrendingDown,
  Eye,
  ChevronDown
} from 'lucide-react';

// Import credit screens
import LeveragedLoansScreen from './credit/LeveragedLoansScreen';
import CreditOperatingSystemDashboard from './credit/CreditOperatingSystemDashboard';
import CLOManagementScreen from './credit/CLOManagementScreen';
import CDSTradingScreen from './credit/CDSTradingScreen';
import CovenantMonitoringScreen from './credit/CovenantMonitoringScreen';
import DistressedDebtScreen from './credit/DistressedDebtScreen';
import DistressedSituationDetailScreen from './credit/DistressedSituationDetailScreen';
import CreditWatchlistScreen from './credit/CreditWatchlistScreen';
import CounterpartyManagementScreen from './credit/CounterpartyManagementScreen';
import SecondaryMovedCard from './credit/SecondaryMovedCard';
import ExposureClassificationScreen from './credit/ExposureClassificationScreen';
import CMBSManagementScreen from './credit/CMBSManagementScreen';
import RMBSManagementScreen from './credit/RMBSManagementScreen';
import ABSManagementScreen from './credit/ABSManagementScreen';
import MSRManagementScreen from './credit/MSRManagementScreen';
import MortgageReitDashboard from './credit/MortgageReitDashboard';
import PrepaymentForecastPanel from './credit/PrepaymentForecastPanel';
import CrossVehicleExposureScreen from './credit/CrossVehicleExposureScreen';
import TrusteeReconciliationScreen from './credit/TrusteeReconciliationScreen';
import AbfHome from '../features/abf/AbfHome';
import { AxiomMetricCard, AxiomMetricGrid } from './shared';
import {
  CollapsedRail,
  CollapsedRailItem,
  CollapsedRailSection,
  CreditWorkspaceBody,
  CreditWorkspaceHero,
  CreditWorkspaceShell,
  Eyebrow,
  HeroSubtitle,
  HeroTitle,
  HeroTop,
  PanelBody,
  PanelHeader,
  PanelSubtitle,
  PanelTitle,
  RailItem,
  RailItemDescription,
  RailItemLabel,
  RailChevron,
  RailSection,
  RailSectionHeader,
  RailSectionLabel,
  RailSectionMeta,
  ViewPill,
  ViewSwitch,
  WorkspaceMain,
  WorkspacePanel,
  WorkspaceRail,
  WorkspaceRailToggle,
} from './credit/shared/CreditWorkspace';

type CreditSubTab = 'overview' | 'credit-os' | 'loans' | 'abf' | 'abs' | 'clo' | 'clo-decisions' | 'cmbs' | 'rmbs' | 'msr' | 'cds' | 'covenants' | 'counterparties' | 'watchlist' | 'distressed' | 'distressed-detail' | 'secondary' | 'exposure' | 'prepayment' | 'cross-vehicle' | 'trustee-recon' | 'reit-book';
type WorkspaceView = 'pm' | 'risk' | 'ops';
type WorkspaceItem = { id: CreditSubTab; label: string; description: string; icon: React.ReactNode };
type RailTone = 'blue' | 'cyan' | 'emerald' | 'amber' | 'red' | 'violet' | 'slate';

const formatCurrency = (value: number) => {
  const v = value ?? 0;
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  if (abs >= 1000000000) return `${sign}$${(abs / 1000000000).toFixed(1)}B`;
  // One decimal below $10M so small risk figures (VaR/DV01) keep precision;
  // whole millions above that ($386M, not $386.0M).
  if (abs >= 1000000) return `${sign}$${(abs / 1000000).toFixed(abs < 10000000 ? 1 : 0)}M`;
  return `${sign}$${Math.round(abs).toLocaleString()}`;
};

const CreditOverview: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [risk, setRisk] = useState<any>(null);
  const [cashDash, setCashDash] = useState<any>(null);

  useEffect(() => {
    // Fetch all data in parallel. Firm-level aggregates only — every source
    // here is a working firm-wide endpoint (see F-CL-001..003 remediation).
    axios.get('/api/credit/dashboard').then(res => setStats(res.data)).catch(() => {});
    axios.get('/api/risk/summary').then(res => setRisk(res.data)).catch(() => {});
    axios.get('/api/cash/dashboard').then(res => setCashDash(res.data)).catch(() => {});
  }, []);

  const loans = stats?.leveragedLoans;
  const clo = stats?.clo;
  const cds = stats?.cds;
  const covenants = stats?.covenants;
  const distressed = stats?.distressed;
  const secondary = stats?.secondaryMarket;

  // "positions" means held/active loans, not the master loan count.
  const activeLoans = loans?.activeLoans ?? loans?.totalLoans ?? 0;
  const cloDeals = clo?.totalDeals ?? 0;
  const covenantAlerts = (covenants?.activeBreaches ?? covenants?.breachCount ?? 0) + (covenants?.warnings ?? covenants?.warningCount ?? 0);
  const cdsContracts = cds?.totalContracts ?? 0;
  const cdsNet = cds?.netExposure ?? cds?.netNotional ?? 0;
  const distressedActive = distressed?.activeSituations ?? 0;
  const distressedExposure = distressed?.totalExposure ?? 0;

  // Firm risk aggregates — field names match the RiskSummary contract
  // (aggregateVar1Day99 / aggregateDV01), and the aggregate is only as complete
  // as the EOD snapshots behind it, so surface the coverage (F-CL-001).
  const firmVaR = risk?.aggregateVar1Day99 ?? 0;
  const firmDV01 = risk?.aggregateDV01 ?? 0;
  const riskPortfolios: any[] = Array.isArray(risk?.portfolios) ? risk.portfolios : [];
  const durationBook = riskPortfolios.filter(p => p?.snapshotAvailable && (p?.totalMarketValue ?? 0) > 0);
  const durationDenom = durationBook.reduce((s, p) => s + (p.totalMarketValue ?? 0), 0);
  const firmDuration = durationDenom > 0
    ? durationBook.reduce((s, p) => s + (p.duration ?? 0) * (p.totalMarketValue ?? 0), 0) / durationDenom
    : 0;
  const snapshotsAvailable = risk?.availableSnapshotCount ?? durationBook.length;
  const snapshotsTotal = riskPortfolios.length || (risk?.availableSnapshotCount ?? 0) + (risk?.missingSnapshotCount ?? 0);
  const partialRisk = risk?.summaryStatus === 'PARTIAL_SNAPSHOT' && snapshotsTotal > 0;
  const riskCoverage = partialRisk ? ` · ${snapshotsAvailable}/${snapshotsTotal} funds` : '';

  // No firm NAV source exists (no nav_calculation); the honest firm-level figure
  // is aggregate marked market value from the risk summary (F-CL-002).
  const creditBookMV = risk?.aggregateTotalMarketValue ?? 0;

  // Firm cash & liquidity — single working endpoint (F-CL-003).
  const liquidity = cashDash?.liquidity;
  const totalCash = liquidity?.totalAvailableCash ?? 0;
  const totalPending = liquidity?.totalPendingSettlements ?? 0;
  const projectedInflows = liquidity?.projectedInflows30d ?? 0;
  const projectedOutflows = liquidity?.projectedOutflows30d ?? 0;
  const fundsBelowMin = liquidity?.portfoliosBelowMinimum ?? 0;
  const liquidityAlerts = cashDash?.alertsHigh ?? 0;

  return (
    <div className="p-6">
      {/* Row 1: Key Metrics */}
      <AxiomMetricGrid style={{ marginBottom: '1.5rem' }}>
        <AxiomMetricCard label="Credit Book MV" value={creditBookMV > 0 ? formatCurrency(creditBookMV) : '--'} subtext="Marked · across funds" icon={<Landmark />} tone="cyan" />
        <AxiomMetricCard label="Available Cash" value={totalCash > 0 ? formatCurrency(totalCash) : '--'} subtext={fundsBelowMin > 0 ? `${fundsBelowMin} fund${fundsBelowMin > 1 ? 's' : ''} below min` : `${formatCurrency(totalPending)} pending`} icon={<RefreshCw />} tone={fundsBelowMin > 0 ? 'amber' : 'emerald'} />
        <AxiomMetricCard label="VaR (1D 99%)" value={firmVaR > 0 ? formatCurrency(firmVaR) : '--'} subtext={`Parametric${riskCoverage}`} icon={<Radar />} tone="violet" />
        <AxiomMetricCard label="DV01" value={Number.isFinite(firmDV01) && firmDV01 !== 0 ? formatCurrency(firmDV01) : '--'} subtext={`Duration ${firmDuration !== 0 ? firmDuration.toFixed(2) + 'yr' : '--'}${riskCoverage}`} icon={<BarChart3 />} tone="cyan" />
        <AxiomMetricCard label="Covenant Alerts" value={covenantAlerts} subtext={`${covenants?.totalCovenants ?? 0} total covenants`} icon={<AlertTriangle />} tone={covenantAlerts > 0 ? 'amber' : 'emerald'} />
        <AxiomMetricCard label="Distressed" value={distressedActive} subtext={`${formatCurrency(distressedExposure)} · tracked situations`} icon={<FileWarning />} tone="red" />
      </AxiomMetricGrid>

      {/* Row 2: Portfolio Breakdown + CDS + Cash */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Portfolio Breakdown */}
        <div className="bg-slate-800/30 border border-white/5 rounded-xl p-5">
          <h3 className="text-sm font-bold text-white mb-3 uppercase tracking-wider">Portfolio Summary</h3>
          <div className="space-y-2.5">
            <div className="flex justify-between items-center">
              <span className="text-slate-400 text-sm">Leveraged Loans</span>
              <span className="text-white font-semibold text-sm">{activeLoans} positions</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400 text-sm">CLO Deals</span>
              <span className="text-white font-semibold text-sm">{cloDeals} deals</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400 text-sm">CDS Contracts</span>
              <span className="text-white font-semibold text-sm">{cdsContracts} contracts</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400 text-sm">Active BWICs</span>
              <span className="text-white font-semibold text-sm">{secondary?.activeListCount ?? 0} lists</span>
            </div>
            <div className="flex justify-between items-center border-t border-white/10 pt-2 mt-2">
              <span className="text-slate-400 text-sm">Counterparties</span>
              <span className="text-white font-semibold text-sm">{stats?.counterparties?.activeCounterparties ?? 0} active</span>
            </div>
          </div>
        </div>

        {/* CDS Net Exposure */}
        <div className="bg-slate-800/30 border border-white/5 rounded-xl p-5">
          <h3 className="text-sm font-bold text-white mb-3 uppercase tracking-wider flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-blue-400" /> CDS Net Exposure
          </h3>
          {cdsContracts > 0 ? (
            <div className="space-y-2.5">
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">Protection Bought</span>
                <span className="text-red-400 font-semibold text-sm">{formatCurrency(cds?.boughtProtectionNotional ?? 0)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">Protection Sold</span>
                <span className="text-green-400 font-semibold text-sm">{formatCurrency(cds?.soldProtectionNotional ?? 0)}</span>
              </div>
              <div className="flex justify-between items-center border-t border-white/10 pt-2 mt-2">
                <span className="text-slate-400 text-sm font-semibold">Net Exposure</span>
                <span className={`font-bold text-sm ${cdsNet >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {cdsNet >= 0 ? '+' : '-'}{formatCurrency(Math.abs(cdsNet))}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">Mark-to-Market</span>
                <span className={`font-semibold text-sm ${(cds?.totalMtm ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {(cds?.totalMtm ?? 0) >= 0 ? '+' : ''}{formatCurrency(cds?.totalMtm ?? 0)}
                </span>
              </div>
            </div>
          ) : (
            <div className="text-center py-6 text-slate-500 text-sm">No CDS positions</div>
          )}
        </div>

        {/* Cash & Liquidity */}
        <div className="bg-slate-800/30 border border-white/5 rounded-xl p-5">
          <h3 className="text-sm font-bold text-white mb-3 uppercase tracking-wider flex items-center gap-2">
            <Landmark className="w-4 h-4 text-green-400" /> Cash & Liquidity
          </h3>
          <div className="space-y-2.5">
            <div className="flex justify-between items-center">
              <span className="text-slate-400 text-sm">Available Cash</span>
              <span className="text-green-400 font-semibold text-sm">{formatCurrency(totalCash)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400 text-sm">Pending Settlements</span>
              <span className="text-amber-400 font-semibold text-sm">{formatCurrency(totalPending)}</span>
            </div>
            <div className="flex justify-between items-center border-t border-white/10 pt-2 mt-2">
              <span className="text-slate-400 text-sm">Projected Inflows (30d)</span>
              <span className="text-green-400 font-semibold text-sm">+{formatCurrency(projectedInflows)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400 text-sm">Projected Outflows (30d)</span>
              <span className="text-red-400 font-semibold text-sm">{formatCurrency(-Math.abs(projectedOutflows))}</span>
            </div>
            {(fundsBelowMin > 0 || liquidityAlerts > 0) && (
              <div className="flex justify-between items-center border-t border-white/10 pt-2 mt-2">
                <span className="text-amber-400 text-sm font-semibold">Liquidity Alerts</span>
                <span className="text-amber-400 font-bold text-sm">{Math.max(fundsBelowMin, liquidityAlerts)}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Row 3: Distressed + CLO Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-slate-800/30 border border-white/5 rounded-xl p-5">
          <h3 className="text-sm font-bold text-white mb-3 uppercase tracking-wider flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" /> Distressed Situations
          </h3>
          {distressedActive > 0 ? (
            <div className="space-y-2.5">
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">Active Situations</span>
                <span className="text-white font-semibold text-sm">{distressedActive}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">Chapter 11</span>
                <span className="text-white font-semibold text-sm">{distressed?.chapter11Count ?? 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">Out of Court</span>
                <span className="text-white font-semibold text-sm">{distressed?.outOfCourtCount ?? 0}</span>
              </div>
              <div className="flex justify-between items-center border-t border-white/10 pt-2 mt-2">
                <span className="text-slate-400 text-sm font-semibold">Tracked Exposure</span>
                <span className="text-amber-400 font-bold text-sm">{formatCurrency(distressedExposure)}</span>
              </div>
            </div>
          ) : (
            <div className="text-center py-6 text-slate-500 text-sm">No active distressed situations</div>
          )}
        </div>

        <div className="bg-slate-800/30 border border-white/5 rounded-xl p-5">
          <h3 className="text-sm font-bold text-white mb-3 uppercase tracking-wider flex items-center gap-2">
            <Layers className="w-4 h-4 text-emerald-400" /> CLO Portfolio
          </h3>
          {cloDeals > 0 ? (
            <div className="space-y-2.5">
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">Total Deals</span>
                <span className="text-white font-semibold text-sm">{cloDeals}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">In Reinvestment</span>
                <span className="text-green-400 font-semibold text-sm">{clo?.dealsInReinvestment ?? clo?.activeDeals ?? 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">Total AUM</span>
                <span className="text-white font-semibold text-sm">{formatCurrency(clo?.totalAum ?? 0)}</span>
              </div>
              <div className="flex justify-between items-center border-t border-white/10 pt-2 mt-2">
                <span className="text-slate-400 text-sm">Distinct Managers</span>
                <span className="text-white font-semibold text-sm">{clo?.distinctManagers ?? 0}</span>
              </div>
            </div>
          ) : (
            <div className="text-center py-6 text-slate-500 text-sm">No CLO positions</div>
          )}
        </div>
      </div>
    </div>
  );
};

const VALID_SUB_TABS: CreditSubTab[] = ['clo-decisions', 'overview', 'credit-os', 'loans', 'abf', 'abs', 'clo', 'cmbs', 'rmbs', 'msr', 'cds', 'covenants', 'counterparties', 'watchlist', 'distressed', 'distressed-detail', 'secondary', 'exposure', 'prepayment', 'cross-vehicle', 'trustee-recon', 'reit-book'];

const CreditLoansTab: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const subFromUrl = searchParams.get('sub') as CreditSubTab | null;
  const initialSubTab: CreditSubTab =
    subFromUrl && VALID_SUB_TABS.includes(subFromUrl) ? subFromUrl : 'overview';
  const [chosenSubTab, setActiveSubTab] = useState<CreditSubTab>(initialSubTab);
  // The URL decides what is shown, in the same render: following it through an effect showed the
  // previous sub-tab for a moment (a leveraged loan link flashed the loan list before its loan page).
  const activeSubTab: CreditSubTab = subFromUrl && VALID_SUB_TABS.includes(subFromUrl) ? subFromUrl : chosenSubTab;
  const [workspaceView, setWorkspaceView] = useState<WorkspaceView>('pm');
  const [railOpen, setRailOpen] = useState({ assetClasses: true, riskOps: true });
  const [railCollapsed, setRailCollapsed] = useState(false);

  // Keep state in sync if URL changes (e.g. deep-link from another pillar)
  useEffect(() => {
    if (subFromUrl && VALID_SUB_TABS.includes(subFromUrl) && subFromUrl !== chosenSubTab) {
      setActiveSubTab(subFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subFromUrl]);

  const handleSubTabChange = (
    next: CreditSubTab,
    context?: Record<string, string | number | undefined>
  ) => {
    setActiveSubTab(next);
    const params = new URLSearchParams(searchParams);
    params.set('sub', next);
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        if (value == null || value === '') params.delete(key);
        else params.set(key, String(value));
      });
    }
    // Don't clobber other params (scope, tab) — covenant deep-links carry scope=...
    setSearchParams(params, { replace: true });
  };

  const viewConfig: Record<WorkspaceView, {
    label: string;
    defaultTab: CreditSubTab;
    copy: string;
    assetOrder: CreditSubTab[];
    analyticsOrder: CreditSubTab[];
  }> = {
    pm: {
      label: 'PM View',
      defaultTab: 'overview',
      copy: 'Prioritises exposure, liquidity, capacity and lifecycle actions.',
      assetOrder: ['overview', 'credit-os', 'abf', 'abs', 'clo', 'loans', 'cmbs', 'rmbs', 'msr', 'cds', 'watchlist', 'distressed'],
      analyticsOrder: ['cross-vehicle', 'secondary', 'covenants', 'counterparties', 'exposure', 'prepayment', 'trustee-recon'],
    },
    risk: {
      label: 'Risk View',
      defaultTab: 'covenants',
      copy: 'Prioritises covenants, trend deterioration, concentration and breach forecast.',
      assetOrder: ['watchlist', 'distressed', 'abf', 'abs', 'loans', 'clo', 'cmbs', 'rmbs', 'msr', 'cds', 'overview', 'credit-os'],
      analyticsOrder: ['covenants', 'exposure', 'cross-vehicle', 'prepayment', 'counterparties', 'trustee-recon', 'secondary'],
    },
    ops: {
      label: 'Ops View',
      defaultTab: 'trustee-recon',
      copy: 'Prioritises reconciliation, trustee breaks, booking status and audit trail.',
      assetOrder: ['abf', 'abs', 'clo', 'cmbs', 'rmbs', 'loans', 'cds', 'overview', 'credit-os', 'watchlist', 'distressed'],
      analyticsOrder: ['trustee-recon', 'counterparties', 'secondary', 'covenants', 'cross-vehicle', 'exposure', 'prepayment'],
    },
  };

  const handleWorkspaceViewChange = (next: WorkspaceView) => {
    setWorkspaceView(next);
    handleSubTabChange(viewConfig[next].defaultTab);
  };

  const orderTabs = (tabs: WorkspaceItem[], order: CreditSubTab[]) => {
    const byId = new Map(tabs.map(tab => [tab.id, tab]));
    const ordered = order.map(id => byId.get(id)).filter(Boolean) as WorkspaceItem[];
    const remaining = tabs.filter(tab => !order.includes(tab.id));
    return [...ordered, ...remaining];
  };

  const assetClassTabs: WorkspaceItem[] = [
    { id: 'overview', label: 'Overview', description: 'Credit book command view', icon: <BarChart3 /> },
    { id: 'credit-os', label: 'Operating System', description: 'Loan-tape import, Credit Book, lifecycle queue, sponsor concentration', icon: <Layers /> },
    { id: 'loans', label: 'Leveraged Loans', description: 'Syndicated and private loan positions', icon: <Landmark /> },
    { id: 'abf', label: 'ABF', description: 'Borrowing-base facilities and pool surveillance', icon: <Layers /> },
    { id: 'abs', label: 'ABS', description: 'Tranches, trustee remittances, triggers and Intex analytics', icon: <Layers /> },
    { id: 'clo', label: 'CLO', description: 'Tranche positions, tests, manager views', icon: <Layers /> },
    { id: 'cmbs', label: 'CMBS', description: 'CRE securitisation exposure and waterfall', icon: <Building2 /> },
    { id: 'rmbs', label: 'RMBS', description: 'Mortgage pools, prepay, loss and surveillance', icon: <Landmark /> },
    { id: 'msr', label: 'MSR', description: 'Servicing-fee strips — negative-duration IO economics', icon: <Landmark /> },
    { id: 'cds', label: 'CDS', description: 'Protection, counterparty and MTM exposure', icon: <RefreshCw /> },
    { id: 'watchlist', label: 'Watchlist', description: 'Names requiring PM/risk attention', icon: <Eye /> },
    { id: 'distressed', label: 'Distressed', description: 'Situations, claims and recovery paths', icon: <AlertTriangle /> },
  ];

  const analyticsTabs: WorkspaceItem[] = [
    { id: 'covenants', label: 'Covenants', description: 'Unified tests, cushions and trends', icon: <FileWarning /> },
    { id: 'counterparties', label: 'Counterparties', description: 'Broker, swap and financing exposure', icon: <Users /> },
    { id: 'exposure', label: 'Exposure', description: 'Issuer, sector and asset-class classification', icon: <Radar /> },
    { id: 'cross-vehicle', label: 'Cross-Vehicle', description: 'Issuer exposure across funds and vehicles', icon: <Building2 /> },
    { id: 'trustee-recon', label: 'Trustee Recon', description: 'Structured-credit trustee breaks', icon: <FileWarning /> },
    { id: 'secondary', label: 'Secondary', description: 'Moved — auctions in Construction, history in Blotter, settlement in Middle Office', icon: <Target /> },
    { id: 'prepayment', label: 'Prepayment', description: 'CPR/CDR forecasts and cohort effects', icon: <TrendingDown /> },
    { id: 'reit-book', label: 'REIT Book', description: 'Levered MBS book — duration, leverage, NAV shock', icon: <BarChart3 /> },
  ];

  const activeView = viewConfig[workspaceView];
  const assetClassTabsForView = orderTabs(assetClassTabs, activeView.assetOrder);
  const analyticsTabsForView = orderTabs(analyticsTabs, activeView.analyticsOrder);
  const allWorkspaceItems = [...assetClassTabs, ...analyticsTabs];
  const activeMeta = allWorkspaceItems.find(tab => tab.id === activeSubTab) || assetClassTabs[0];
  const assetTone = (tab: WorkspaceItem): RailTone =>
    tab.id === 'abf' ? 'cyan' : tab.id === 'distressed' ? 'amber' : 'blue';
  const analyticsTone = (tab: WorkspaceItem): RailTone =>
    tab.id === 'covenants' ? 'amber' : tab.id === 'exposure' ? 'violet' : 'cyan';
  const renderCollapsedRailItems = (items: WorkspaceItem[], toneForTab: (tab: WorkspaceItem) => RailTone) => (
    <CollapsedRailSection>
      {items.map(tab => (
        <CollapsedRailItem
          key={tab.id}
          $active={activeSubTab === tab.id}
          $tone={toneForTab(tab)}
          aria-label={tab.label}
          title={`${tab.label} - ${tab.description}`}
          onClick={() => handleSubTabChange(tab.id)}
        >
          {tab.icon}
        </CollapsedRailItem>
      ))}
    </CollapsedRailSection>
  );

  // A link from before Loan 360 had its own page goes straight to it, without drawing the desk first.
  const linkedLoanSource = (searchParams.get('loanSource') || '').toUpperCase();
  if (activeSubTab === 'credit-os' && ['PRIVATE_CREDIT', 'LEVERAGED_LOAN'].includes(linkedLoanSource) && searchParams.get('loanId')) {
    return <Navigate replace to={loan360Path(linkedLoanSource, searchParams.get('loanId') as string, searchParams.get('loanTab') || 'overview')} />;
  }

  return (
    <CreditWorkspaceShell>
      <CreditWorkspaceHero>
        <HeroTop>
          <div>
            <Eyebrow>Credit Workspace</Eyebrow>
            <HeroTitle>Credit & Loans Control Room</HeroTitle>
            <HeroSubtitle>
              One operating surface for credit exposure, asset-class workbenches, covenant surveillance,
              cross-vehicle concentration, trustee breaks and lifecycle actions.
            </HeroSubtitle>
          </div>
          <ViewSwitch aria-label="Credit workspace view">
            <ViewPill $active={workspaceView === 'pm'} onClick={() => handleWorkspaceViewChange('pm')}>PM View</ViewPill>
            <ViewPill $active={workspaceView === 'risk'} onClick={() => handleWorkspaceViewChange('risk')}>Risk View</ViewPill>
            <ViewPill $active={workspaceView === 'ops'} onClick={() => handleWorkspaceViewChange('ops')}>Ops View</ViewPill>
          </ViewSwitch>
        </HeroTop>
        <HeroSubtitle>{activeView.copy}</HeroSubtitle>
      </CreditWorkspaceHero>

      <CreditWorkspaceBody $railCollapsed={railCollapsed}>
        <WorkspaceRail $collapsed={railCollapsed}>
          <WorkspaceRailToggle collapsed={railCollapsed} onToggle={() => setRailCollapsed(prev => !prev)} />
          {railCollapsed ? (
            <CollapsedRail>
              {renderCollapsedRailItems(assetClassTabsForView, assetTone)}
              {renderCollapsedRailItems(analyticsTabsForView, analyticsTone)}
            </CollapsedRail>
          ) : (
            <>
              <RailSection>
                <RailSectionHeader
                  type="button"
                  aria-expanded={railOpen.assetClasses}
                  onClick={() => setRailOpen(prev => ({ ...prev, assetClasses: !prev.assetClasses }))}
                >
                  <RailSectionLabel>Asset Classes</RailSectionLabel>
                  <RailSectionMeta>
                    {assetClassTabsForView.length}
                    <RailChevron $open={railOpen.assetClasses}><ChevronDown /></RailChevron>
                  </RailSectionMeta>
                </RailSectionHeader>
                <AnimatePresence initial={false}>
                  {railOpen.assetClasses && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.16 }}
                      style={{ overflow: 'hidden' }}
                    >
                      {assetClassTabsForView.map(tab => (
                        <RailItem
                          key={tab.id}
                          $active={activeSubTab === tab.id}
                          $tone={assetTone(tab)}
                          onClick={() => handleSubTabChange(tab.id)}
                        >
                          {tab.icon}
                          <div>
                            <RailItemLabel>{tab.label}</RailItemLabel>
                            <RailItemDescription>{tab.description}</RailItemDescription>
                          </div>
                        </RailItem>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </RailSection>
              <RailSection>
                <RailSectionHeader
                  type="button"
                  aria-expanded={railOpen.riskOps}
                  onClick={() => setRailOpen(prev => ({ ...prev, riskOps: !prev.riskOps }))}
                >
                  <RailSectionLabel>Risk & Operations</RailSectionLabel>
                  <RailSectionMeta>
                    {analyticsTabsForView.length}
                    <RailChevron $open={railOpen.riskOps}><ChevronDown /></RailChevron>
                  </RailSectionMeta>
                </RailSectionHeader>
                <AnimatePresence initial={false}>
                  {railOpen.riskOps && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.16 }}
                      style={{ overflow: 'hidden' }}
                    >
                      {analyticsTabsForView.map(tab => (
                        <RailItem
                          key={tab.id}
                          $active={activeSubTab === tab.id}
                          $tone={analyticsTone(tab)}
                          onClick={() => handleSubTabChange(tab.id)}
                        >
                          {tab.icon}
                          <div>
                            <RailItemLabel>{tab.label}</RailItemLabel>
                            <RailItemDescription>{tab.description}</RailItemDescription>
                          </div>
                        </RailItem>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </RailSection>
            </>
          )}
        </WorkspaceRail>

        <WorkspaceMain>
          <WorkspacePanel>
            <PanelHeader>
              <div>
                <PanelTitle>{activeMeta.label}</PanelTitle>
                <PanelSubtitle>{activeMeta.description} · {activeView.label}</PanelSubtitle>
              </div>
              {activeSubTab === 'clo' && <Link to="/portfolio-management?tab=construction&lane=builder&clo=1" style={{color:'#a5b4fc',fontSize:13,fontWeight:600}}>Buy a loan into a managed CLO →</Link>}
            </PanelHeader>
            <PanelBody>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeSubTab}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  transition={{ duration: 0.15 }}
                >
                  {activeSubTab === 'overview' && <CreditOverview />}
                  {activeSubTab === 'credit-os' && <CreditOperatingSystemDashboard onNavigate={(t, context) => handleSubTabChange(t as CreditSubTab, context)} />}
                  {activeSubTab === 'loans' && <LeveragedLoansScreen embedded />}
                  {activeSubTab === 'abf' && <AbfHome embedded />}
                  {activeSubTab === 'abs' && <ABSManagementScreen embedded />}
                  {activeSubTab === 'clo' && <CLOManagementScreen embedded />}
                  {activeSubTab === 'clo-decisions' && <Navigate replace to="/portfolio-management?tab=construction&lane=builder&clo=1" />}
                  {activeSubTab === 'cmbs' && <CMBSManagementScreen embedded />}
                  {activeSubTab === 'rmbs' && <RMBSManagementScreen embedded />}
                  {activeSubTab === 'msr' && <MSRManagementScreen embedded />}
                  {activeSubTab === 'cds' && <CDSTradingScreen embedded />}
                  {activeSubTab === 'covenants' && <CovenantMonitoringScreen embedded />}
                  {activeSubTab === 'counterparties' && <CounterpartyManagementScreen embedded />}
                  {activeSubTab === 'watchlist' && <CreditWatchlistScreen embedded />}
                  {activeSubTab === 'distressed' && <DistressedDebtScreen embedded />}
                  {activeSubTab === 'distressed-detail' && <DistressedSituationDetailScreen embedded />}
                  {activeSubTab === 'secondary' && <SecondaryMovedCard />}
                  {activeSubTab === 'exposure' && <ExposureClassificationScreen />}
                  {activeSubTab === 'cross-vehicle' && <CrossVehicleExposureScreen />}
                  {activeSubTab === 'trustee-recon' && <TrusteeReconciliationScreen />}
                  {activeSubTab === 'prepayment' && <PrepaymentForecastPanel />}
                  {activeSubTab === 'reit-book' && <MortgageReitDashboard embedded />}
                </motion.div>
              </AnimatePresence>
            </PanelBody>
          </WorkspacePanel>
        </WorkspaceMain>
      </CreditWorkspaceBody>
    </CreditWorkspaceShell>
  );
};

export default CreditLoansTab;
